import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { OrdenPedido } from '../types/ordenPedido.types';
import { OrdenPedidoService } from '../services/ordenPedidoService';

interface OrdenPedidoState {
  ordenes: OrdenPedido[];
  selectedOrden: OrdenPedido | null;
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;

  fetchOrdenes: () => Promise<void>;
  fetchOrdenById: (id: number) => Promise<void>;
  saveOrden: (data: OrdenPedido) => Promise<void>;
  deleteOrden: (id: number) => Promise<void>;
  setSelectedOrden: (orden: OrdenPedido | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  ordenes: [],
  selectedOrden: null,
  loading: false,
  error: null,
  dialogOpen: false,
};

export const useOrdenPedidoStore = create<OrdenPedidoState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchOrdenes: async () => {
        set({ loading: true, error: null }, false, 'ordenPedido/fetchAll/start');
        try {
          const ordenes = await OrdenPedidoService.getAll();
          set({ ordenes, loading: false }, false, 'ordenPedido/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar ordenes' }, false, 'ordenPedido/fetchAll/error');
        }
      },

      fetchOrdenById: async (id: number) => {
        set({ loading: true, error: null }, false, 'ordenPedido/fetchById/start');
        try {
          const orden = await OrdenPedidoService.getById(id);
          set({ selectedOrden: orden, loading: false }, false, 'ordenPedido/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar orden' }, false, 'ordenPedido/fetchById/error');
        }
      },

      saveOrden: async (data: OrdenPedido) => {
        set({ loading: true, error: null }, false, 'ordenPedido/save/start');
        try {
          await OrdenPedidoService.save(data);
          set({ loading: false, dialogOpen: false }, false, 'ordenPedido/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar orden' }, false, 'ordenPedido/save/error');
          throw error;
        }
      },

      deleteOrden: async (id: number) => {
        set({ loading: true, error: null }, false, 'ordenPedido/delete/start');
        try {
          await OrdenPedidoService.delete(id);
          set((state) => ({
            ordenes: state.ordenes.filter((o) => o.idOrdenPedido !== id),
            loading: false,
          }), false, 'ordenPedido/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar orden' }, false, 'ordenPedido/delete/error');
        }
      },

      setSelectedOrden: (orden) => set({ selectedOrden: orden }, false, 'ordenPedido/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'ordenPedido/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedOrden: null }, false, 'ordenPedido/closeDialog'),
      clearError: () => set({ error: null }, false, 'ordenPedido/clearError'),
      reset: () => set(initialState, false, 'ordenPedido/reset'),
    }),
    { name: 'orden-pedido-store' }
  )
);
